import {
  RiseQuestionImagePicker,
  RiseQuestionRadio,
  RiseQuestionText,
  RiseSurveyPage,
  RiseQuestionCheckbox,
  RiseQuestionDropdown,
  CategoryType,
} from '../../../types';
import {getRandomDelay} from './getRandomDelay';

// TODO combine PageOptions and RadioOptions they are basically the same?
export type PageOptions = {
  category?: CategoryType;
  /** How long to show the spinner for before moving to the next page */
  delayDuration?: number | 'random';
  /** If true, the page will be skipped if the visibleIf condition is not met */
  visibleIf?: string;
  /** Callback function that will be called when the user advances from this page */
  onNext?: (surveyData: any) => void | Promise<void>;
  autoAdvance?: boolean;
  hideProgressBar?: boolean;
  hideCategory?: boolean;
  identifyPropertyName?: string;
  showLogo?: boolean;
  theme?: string;
};

export const page = (
  question:
    | RiseQuestionRadio
    | RiseQuestionText
    | RiseQuestionImagePicker
    | RiseQuestionCheckbox
    | RiseQuestionDropdown,
  options: PageOptions = {},
): RiseSurveyPage => {
  return {
    name: 'page-' + question.name,
    elements: [question],
    visibleIf: options.visibleIf,
    riseProperties: {
      category: options.category,
      delayOnNext: options.delayDuration === 'random' ? getRandomDelay() : (options.delayDuration ?? 0),
      onNext: options.onNext,
      autoAdvance: options.autoAdvance,
      hideProgressBar: options.hideProgressBar,
      hideCategory: options.hideCategory,
      identifyPropertyName: options.identifyPropertyName,
      showLogo: options.showLogo,
      theme: options.theme,
    },
  };
};

type TextOptions = Omit<RiseQuestionText, 'type'> & {
  /** How long to show the spinner for before moving to the next page. `random` will show a random delay between 1 and 5 seconds. */
  delay?: 'random' | number | undefined;
  /** If true, the page will be skipped if the visibleIf condition is not met */
  visibleIf?: string;
  category?: CategoryType;
  hideProgressBar?: boolean;
  hideCategory?: boolean;
  showLogo?: boolean;
  theme?: string;
};

type RadioOptions = Omit<RiseQuestionRadio, 'type'> & {
  /** How long to show the spinner for before moving to the next page. `random` will show a random delay between 1 and 5 seconds. */
  delay?: 'random' | number | undefined;
  /** If true, the page will be skipped if the visibleIf condition is not met */
  visibleIf?: string;
  /** Callback function that will be called when the user advances from this page */
  onNext?: (surveyData: any) => void | Promise<void>;
  category?: CategoryType;
  autoAdvance?: boolean;
  hideProgressBar?: boolean;
  hideCategory?: boolean;
  identifyPropertyName?: string;
  showLogo?: boolean;
  theme?: string;
};

type DropdownOptions = Omit<RiseQuestionDropdown, 'type'> & {
  /** How long to show the spinner for before moving to the next page. `random` will show a random delay between 1 and 5 seconds. */
  delay?: 'random' | number | undefined;
  /** If true, the page will be skipped if the visibleIf condition is not met */
  visibleIf?: string;
  /** Callback function that will be called when the user advances from this page */
  onNext?: (surveyData: any) => void | Promise<void>;
  category?: CategoryType;
  autoAdvance?: boolean;
  hideProgressBar?: boolean;
  hideCategory?: boolean;
  identifyPropertyName?: string;
  showLogo?: boolean;
  theme?: string;
};

type ImagePickerOptions = Omit<RiseQuestionImagePicker, 'type'> & {
  /** How long to show the spinner for before moving to the next page. `random` will show a random delay between 1 and 5 seconds. */
  delay?: 'random' | number | undefined;
  /** If true, the page will be skipped if the visibleIf condition is not met */
  visibleIf?: string;
  autoAdvance?: boolean;
  category?: CategoryType;
  hideProgressBar?: boolean;
  hideCategory?: boolean;
  identifyPropertyName?: string;
  showLogo?: boolean;
  theme?: string;
};

type CheckboxOptions = Omit<RiseQuestionCheckbox, 'type'> & {
  /** How long to show the spinner for before moving to the next page. `random` will show a random delay between 1 and 5 seconds. */
  delay?: 'random' | number | undefined;
  /** If true, the page will be skipped if the visibleIf condition is not met */
  visibleIf?: string;
  category?: CategoryType;
  hideProgressBar?: boolean;
  hideCategory?: boolean;
  identifyPropertyName?: string;
  showLogo?: boolean;
  theme?: string;
};

type InputField = {
  name: string;
  title?: string;
  placeholder?: string;
  isRequired?: boolean;
  /** Input formatting/masking function that receives the input element and question */
  inputMask?: (inputElement: HTMLInputElement, question: any) => void;
};

type InputPageOptions = {
  /** Unique name for the page */
  name: string;
  /** Array of text input fields to display on the page */
  inputs: InputField[];
  /** Page title displayed above the form fields */
  title?: string;
  /** Page description displayed below the title */
  description?: string;
  /** How long to show the spinner for before moving to the next page. `random` will show a random delay between 1 and 5 seconds. */
  delay?: 'random' | number | undefined;
  /** If true, the page will be skipped if the visibleIf condition is not met */
  visibleIf?: string;
  /** Callback function that will be called when the user advances from this page */
  onNext?: (surveyData: any) => void | Promise<void>;
  category?: CategoryType;
  hideProgressBar?: boolean;
  hideCategory?: boolean;
  showLogo?: boolean;
  theme?: string;
};

export const textPage = ({
  visibleIf,
  delay,
  category,
  hideCategory,
  hideProgressBar,
  showLogo,
  theme,
  ...textOptions
}: TextOptions): RiseSurveyPage => {
  return page(
    {
      type: 'text',
      ...textOptions,
    },
    {
      visibleIf,
      delayDuration: delay,
      category,
      hideProgressBar,
      hideCategory,
      showLogo,
      theme,
    },
  );
};

export const imagePickerPage = ({
  visibleIf,
  delay,
  category,
  hideCategory,
  hideProgressBar,
  autoAdvance,
  identifyPropertyName,
  showLogo,
  theme,
  ...imagePickerOptions
}: ImagePickerOptions): RiseSurveyPage => {
  return page(
    {
      type: 'imagepicker',
      ...imagePickerOptions,
    },
    {
      visibleIf,
      delayDuration: delay,
      category,
      autoAdvance,
      hideProgressBar,
      hideCategory,
      identifyPropertyName,
      showLogo,
      theme,
    },
  );
};

export const radioPage = ({
  visibleIf,
  delay,
  onNext,
  category,
  autoAdvance,
  identifyPropertyName,
  hideProgressBar,
  hideCategory,
  showLogo,
  theme,
  ...radioOptions
}: RadioOptions): RiseSurveyPage => {
  return page(
    {
      type: 'radiogroup',
      ...radioOptions,
    },
    {
      visibleIf,
      delayDuration: delay,
      onNext,
      category,
      autoAdvance,
      identifyPropertyName,
      hideProgressBar,
      hideCategory,
      showLogo,
      theme,
    },
  );
};

export const dropdownPage = ({
  visibleIf,
  delay,
  onNext,
  category,
  autoAdvance,
  identifyPropertyName,
  hideProgressBar,
  hideCategory,
  showLogo,
  theme,
  ...dropdownOptions
}: DropdownOptions): RiseSurveyPage => {
  return page(
    {
      type: 'dropdown',
      ...dropdownOptions,
    },
    {
      visibleIf,
      delayDuration: delay,
      onNext,
      category,
      autoAdvance,
      identifyPropertyName,
      hideProgressBar,
      hideCategory,
      showLogo,
      theme,
    },
  );
};

export const checkboxPage = ({
  visibleIf,
  delay,
  category,
  hideProgressBar,
  hideCategory,
  identifyPropertyName,
  showLogo,
  theme,
  ...checkboxOptions
}: CheckboxOptions): RiseSurveyPage => {
  return page(
    {
      type: 'checkbox',
      ...checkboxOptions,
    },
    {
      visibleIf,
      delayDuration: delay,
      category,
      hideProgressBar,
      hideCategory,
      identifyPropertyName,
      showLogo,
      theme,
    },
  );
};

export const inputPage = ({
  name,
  inputs,
  title,
  description,
  visibleIf,
  delay,
  onNext,
  category,
  hideProgressBar,
  hideCategory,
  showLogo,
  theme,
}: InputPageOptions): RiseSurveyPage => {
  const elements: RiseQuestionText[] = inputs.map(input => ({
    type: 'text',
    name: input.name,
    title: input.title ?? '',
    ...(input.title ? {} : {titleLocation: 'hidden' as const}),
    placeholder: input.placeholder,
    isRequired: input.isRequired,
    inputMask: input.inputMask,
  }));

  const surveyPage: RiseSurveyPage = {
    name: 'page-' + name,
    title,
    description,
    elements,
    visibleIf,
    riseProperties: {
      category,
      delayOnNext: delay === 'random' ? getRandomDelay() : (delay ?? 0),
      onNext,
      hideProgressBar,
      hideCategory,
      showLogo,
      theme,
    },
  };

  return surveyPage;
};
